import { type RecordingUploadTransport, type TemporalUploadTicket } from '../contracts';
import { type StoredBlob } from './packBlob';

export { type RecordingUploadTransport, type TemporalUploadTicket };

/**
 * `failed` retains its blob until logout. Settled rows unblock note generation;
 * only uploaded or abandoned rows are final enough for cleanup.
 */
export type PersistedStatus = 'pending' | 'uploaded' | 'failed' | 'abandoned';

export const SETTLED_STATUSES: readonly PersistedStatus[] = [
  'uploaded',
  'failed',
  'abandoned',
];

export const FINAL_STATUSES: readonly PersistedStatus[] = ['uploaded', 'abandoned'];

export const isSettled = (status: PersistedStatus) => SETTLED_STATUSES.includes(status);

export const isFinal = (status: PersistedStatus) => FINAL_STATUSES.includes(status);

export interface RecordingRow {
  sessionId: string;
  recordingId: string;
  /** Authenticated owner; unowned rows are treated as foreign. */
  userRefId: string;
  startDate: Date;
  endDate?: Date;
  lastChunkIndex: number | null;
  useTranscriptionServiceV2: boolean;
  /** Epoch milliseconds used to order recordings within a session. */
  createdAt: number;
  importedFromLegacy?: boolean;
  /** Whether cleanup must wait for a backup row. */
  backupRequired: boolean;
  /** Missing on rows created before v3 (or on the HTTP path) ⇒ HTTP. */
  uploadTransport?: RecordingUploadTransport;
}

export interface ChunkRow {
  sessionId: string;
  recordingId: string;
  index: number;
  /** Recorder-generated UUID used for logs and debugging. */
  id: string;
  status: PersistedStatus;
  wasMissingChunk: boolean;
  size: number;
  duration: number;
  producedAt: number;
  attempts: number;
  updatedAt: number;
  hasBlob: boolean;
  /** True for an import-generated gap that real data may replace. */
  syntheticGap?: true;
}

export interface ChunkBlobRow {
  sessionId: string;
  recordingId: string;
  index: number;
  blob: StoredBlob;
}

export interface BackupRow {
  sessionId: string;
  recordingId: string;
  status: PersistedStatus;
  size: number;
  attempts: number;
  updatedAt: number;
  hasBlob: boolean;
}

export interface BackupBlobRow {
  sessionId: string;
  recordingId: string;
  blob: StoredBlob;
}

export interface ChunkKey {
  sessionId: string;
  recordingId: string;
  index: number;
}

export interface RecordingKey {
  sessionId: string;
  recordingId: string;
}

/** Metadata needed to create (or upsert-if-missing) a chunk row. */
export interface ChunkMetaInput extends ChunkKey {
  id: string;
  size: number;
  duration: number;
  producedAt: number;
}

export interface RecordingProgress {
  total: number;
  pending: number;
  uploaded: number;
  failed: number;
  abandoned: number;
  /** uploaded + failed + abandoned — the note-generation bar. */
  settled: number;
}

export const chunkPk = (key: ChunkKey): [string, string, number] => [
  key.sessionId,
  key.recordingId,
  key.index,
];

export interface ChunkStatusEntry {
  index: number;
  status: PersistedStatus;
}

/**
 * The database's status monotonicity, shared so the mirror cannot accept a
 * downgrade the database refuses: settled never returns to pending, and only
 * an upload may overwrite failed/abandoned.
 */
export const acceptsChunkStatus = (
  current: PersistedStatus | undefined,
  next: PersistedStatus
): boolean => {
  if (!current || current === next || current === 'pending') return true;
  if (current === 'failed') return next === 'uploaded' || next === 'abandoned';
  return next === 'uploaded' && current !== 'uploaded';
};

/** One progress bar for database and mirror, so the two cannot drift. */
export const aggregateRecordingProgress = (
  entries: readonly ChunkStatusEntry[]
): RecordingProgress => {
  const count = (status: PersistedStatus) =>
    entries.filter((entry) => entry.status === status).length;
  const uploaded = count('uploaded');
  const failed = count('failed');
  const abandoned = count('abandoned');
  return {
    total: entries.length,
    pending: count('pending'),
    uploaded,
    failed,
    abandoned,
    settled: uploaded + failed + abandoned,
  };
};

/** Complete once the final index is known, 0..last all present, all settled. */
export const isUploadCompleteFrom = (
  lastChunkIndex: number | null,
  entries: readonly ChunkStatusEntry[]
): boolean => {
  if (lastChunkIndex === null) return false;
  if (!entries.every((entry) => isSettled(entry.status))) return false;
  const present = new Set(entries.map((entry) => entry.index));
  for (let index = 0; index <= lastChunkIndex; index++) {
    if (!present.has(index)) return false;
  }
  return true;
};

/**
 * First write wins for the recording's lifetime; an existing decision accepts
 * only a same-delivery temporal re-mint (ticket refresh). Shared so the
 * database and the memory mirror can never arbitrate a pin differently.
 */
export const acceptsTransportPin = (
  current: RecordingUploadTransport | undefined,
  next: RecordingUploadTransport
): boolean =>
  !current ||
  (current.kind === 'temporal' &&
    next.kind === 'temporal' &&
    current.deliveryId === next.deliveryId);

/** v3 temporal and the V2 service keep one chunk per (recording, index); V1 stores a fragment per request. */
export const hasIdempotentChunkDelivery = (
  recording: Pick<RecordingRow, 'uploadTransport' | 'useTranscriptionServiceV2'>
): boolean =>
  recording.uploadTransport?.kind === 'temporal' || recording.useTranscriptionServiceV2;

export const recordingPk = (key: RecordingKey): [string, string] => [
  key.sessionId,
  key.recordingId,
];
